import { useState } from "react";
import { Dices, Users, RefreshCw, Repeat } from "lucide-react";
import { api, money } from "../api";
import { addPick, pickListToast } from "../picks";
import { SectionTitle, VegDot } from "../components";
import { ModuleGate, useFlags, flagOn } from "../flags";
import SuggestedMeal, { AltPanel, getMealPrefs, MealPrefsBar } from "../SuggestedMeal";

const Pill = ({ active, children, onClick, testId }) => (
  <button data-testid={testId} onClick={onClick}
    className={`rounded-full px-4 py-2.5 text-xs font-semibold border transition-colors ${active ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-[#1A1A1A] text-white/70 border-white/10"}`}>
    {children}
  </button>
);

const CREW_ROLE_MAP = { Starter: "starter", "Veg Main": "veg-main", "Non-Veg Main": "nonveg-main", Rice: "rice", Bread: "bread", Dessert: "dessert" };

export default function MealBuilderPage() {
  return (
    <ModuleGate flag="meal_builder">
      <MealBuilderInner />
    </ModuleGate>
  );
}

function MealBuilderInner() {
  const flags = useFlags();
  const surpriseOn = flagOn(flags, "surprise_me");
  const crewOn = flagOn(flags, "group_builder");
  const [tab, setTab] = useState("surprise");
  const [prefs, setPrefs] = useState(getMealPrefs());
  const [trigger, setTrigger] = useState(0);
  const [crew, setCrew] = useState({ adults: 4, children: 0, veg_count: 1, nonveg_count: 3, spice: "medium", budget: 150 });
  const [crewMeal, setCrewMeal] = useState(null);
  const [crewReplacing, setCrewReplacing] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildCrew = async () => {
    setLoading(true);
    setCrewReplacing(null);
    const r = await api.post("/crew", crew);
    setCrewMeal(r.data);
    setLoading(false);
  };

  const crewSwap = (idx, alt) => {
    const items = crewMeal.items.map((it, i) => (i === idx ? { ...it, ...alt } : it));
    const total = Math.round(items.reduce((s, i) => s + i.price * (i.qty || 1), 0) * 100) / 100;
    setCrewMeal({ ...crewMeal, items, total, within_budget: total <= crewMeal.budget });
    setCrewReplacing(null);
  };

  return (
    <div data-testid="meal-builder-page" className="px-4 pt-6 pb-10 mx-auto w-full max-w-3xl">
      <SectionTitle kicker="Meal builder" title="Let VeerGi set the table" testId="meal-title" />
      <div className="flex gap-2 mb-6">
        {surpriseOn && <Pill testId="tab-surprise" active={tab === "surprise"} onClick={() => setTab("surprise")}><Dices size={12} className="inline mr-1" /> SURPRISE ME</Pill>}
        {crewOn && <Pill testId="tab-crew" active={tab === "crew"} onClick={() => setTab("crew")}><Users size={12} className="inline mr-1" /> FEED MY CREW</Pill>}
      </div>

      {tab === "surprise" && surpriseOn && (
        <div>
          <MealPrefsBar prefs={prefs} onChange={setPrefs} alwaysOpen />
          <button data-testid="surprise-generate" onClick={() => setTrigger((t) => t + 1)}
            className="w-full bg-[#D4AF37] text-black font-bold rounded-full py-3.5 text-sm tracking-widest hover:bg-[#F1C40F] mb-6 mt-3">
            {trigger > 0 ? "SUGGEST ANOTHER MEAL" : "SUGGEST A MEAL"}
          </button>
          <SuggestedMeal prefs={prefs} trigger={trigger} />
        </div>
      )}

      {tab === "crew" && crewOn && (
        <div>
          {[["adults", "ADULTS"], ["children", "CHILDREN"], ["veg_count", "VEGETARIANS"], ["nonveg_count", "NON-VEG EATERS"]].map(([key, label]) => (
            <div key={key} className="flex items-center justify-between mb-3">
              <span className="text-xs text-white/60 tracking-widest">{label}</span>
              <div className="flex items-center gap-3">
                <button data-testid={`crew-${key}-minus`} onClick={() => setCrew({ ...crew, [key]: Math.max(0, crew[key] - 1) })} className="w-9 h-9 rounded-full bg-[#1A1A1A] border border-white/10 text-lg">−</button>
                <span className="w-6 text-center font-bold" data-testid={`crew-${key}-value`}>{crew[key]}</span>
                <button data-testid={`crew-${key}-plus`} onClick={() => setCrew({ ...crew, [key]: crew[key] + 1 })} className="w-9 h-9 rounded-full bg-[#1A1A1A] border border-white/10 text-lg">+</button>
              </div>
            </div>
          ))}
          <p className="text-[10px] tracking-[0.25em] text-white/40 mb-2 mt-4">SPICE</p>
          <div className="flex gap-2 mb-4">
            {["mild", "medium", "hot"].map((s) => <Pill key={s} testId={`crew-spice-${s}`} active={crew.spice === s} onClick={() => setCrew({ ...crew, spice: s })}>{s.toUpperCase()}</Pill>)}
          </div>
          <p className="text-[10px] tracking-[0.25em] text-white/40 mb-2">BUDGET — {money(crew.budget)}</p>
          <input data-testid="crew-budget" type="range" min="60" max="300" step="10" value={crew.budget} onChange={(e) => setCrew({ ...crew, budget: +e.target.value })} className="w-full accent-[#D4AF37] mb-6" />
          <button data-testid="crew-generate" onClick={buildCrew} disabled={loading}
            className="w-full bg-[#D4AF37] text-black font-bold rounded-full py-3.5 text-sm tracking-widest hover:bg-[#F1C40F] disabled:opacity-60">
            {loading ? "PLANNING…" : `BUILD DINNER FOR ${crew.adults + crew.children}`}
          </button>
          {crewMeal && (
            <div data-testid="crew-result" className="mt-6 rounded-2xl bg-[#121212] border border-[#D4AF37]/30 divide-y divide-white/5">
              {crewMeal.items.map((item, i) => (
                <div key={`${item.slug}-${i}`}>
                  <div className="flex items-center gap-3 p-3">
                    <span className="flex-1 min-w-0">
                      <span className="block text-[9px] tracking-widest text-[#D4AF37] font-bold">{item.role.toUpperCase()}{item.qty > 1 ? ` ×${item.qty}` : ""}</span>
                      <span className="text-xs flex items-start gap-1.5 mt-0.5">
                        <span className="shrink-0 mt-[3px]"><VegDot veg={item.vegetarian} /></span>
                        <span className="line-clamp-3 leading-snug min-w-0">{item.name}</span>
                      </span>
                      <span className="block sm:hidden text-xs text-[#D4AF37] font-bold mt-0.5">{money(item.price * item.qty)}</span>
                    </span>
                    <span className="hidden sm:block text-xs text-[#D4AF37] font-bold shrink-0">{money(item.price * item.qty)}</span>
                    {CREW_ROLE_MAP[item.role] && (
                      <button data-testid={`crew-replace-${i}`} onClick={() => setCrewReplacing(crewReplacing === i ? null : i)}
                        aria-label={`Replace ${item.name}`}
                        className="shrink-0 flex items-center gap-1 text-[9px] font-bold tracking-widest text-white/70 border border-white/20 rounded-full px-2.5 py-1.5">
                        <Repeat size={12} /> <span className="hidden sm:inline">REPLACE</span>
                      </button>
                    )}
                  </div>
                  {crewReplacing === i && (
                    <AltPanel role={CREW_ROLE_MAP[item.role]} current={item} prefs={{ spice: crew.spice }}
                      onSelect={(alt) => crewSwap(i, alt)} onClose={() => setCrewReplacing(null)} />
                  )}
                </div>
              ))}
              <div className="flex justify-between items-center p-4">
                <div>
                  <span data-testid="crew-budget-fit" className="text-sm font-bold">{crewMeal.within_budget ? "FITS YOUR BUDGET" : "OVER BUDGET"}</span>
                  {!crewMeal.within_budget && <p className="text-[10px] text-[#E25822]">Over budget — remove an item or raise budget</p>}
                  <p className="text-[10px] text-white/45 mt-0.5">Tax and anything else is added at the till.</p>
                </div>
              </div>
              <div className="p-3 flex gap-2">
                <button data-testid="crew-change" onClick={buildCrew} className="flex-1 flex items-center justify-center gap-1.5 border border-white/20 rounded-full py-3 text-xs font-bold tracking-widest">
                  <RefreshCw size={12} /> REGENERATE MEAL
                </button>
                <button data-testid="crew-order"
                  onClick={() => {
                    crewMeal.items.forEach((i) => addPick(i, i.qty || 1, true));
                    pickListToast("Crew dinner added to your order list");
                  }}
                  className="flex-1 bg-[#D4AF37] text-black rounded-full py-3 text-xs font-bold tracking-widest">ADD TO ORDER LIST</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
